import { useDebateStore } from "../../store/debateStore";
import type { DebateTopology } from "../../domain/types";
import { useStageLayout, type StageShape } from "../../hooks/useStageLayout";

interface Props {
  /** Container width in px. */
  width: number;
  /** Container height in px (== width for circle, < width for boardroom). */
  height: number;
  shape: StageShape;
  topology: DebateTopology;
  /** Use the previewed seat order when no live debate is hydrated. */
  previewSeatCount?: number;
}

export function TopologyEdgeRenderer({
  width,
  height,
  shape,
  topology,
  previewSeatCount,
}: Props) {
  const seatOrder = useDebateStore((s) => s.seatOrder);
  const personas = useDebateStore((s) => s.personas);
  const currentSpeakerId = useDebateStore((s) => s.currentSpeakerId);

  const seatCount = seatOrder.length > 0 ? seatOrder.length : (previewSeatCount ?? 0);
  // Use the unified stage-layout hook so circle and boardroom modes
  // share one source of truth for seat coordinates. Edges then draw
  // straight lines between seats regardless of underlying topology
  // shape — they were always straight anyway, only the seat layout
  // changes.
  const positions = useStageLayout({ seatCount, width, height, shape });
  if (seatCount === 0) return null;

  const cx = width / 2;
  const cy = height / 2;
  const moderatorIdx = seatOrder.findIndex((id) => personas[id]?.role === "moderator");
  const speakerIdx = currentSpeakerId ? seatOrder.indexOf(currentSpeakerId) : -1;

  const live = seatOrder.length > 0;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden
    >
      <defs>
        <radialGradient id="edge-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="hsl(var(--accent) / 0.65)" />
          <stop offset="100%" stopColor="hsl(var(--accent) / 0)" />
        </radialGradient>
        <linearGradient id="edge-active" x1="0" x2="1">
          <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.85" />
          <stop offset="100%" stopColor="hsl(var(--accent-2))" stopOpacity="0.85" />
        </linearGradient>
      </defs>

      {topology === "round-robin" &&
        positions.map((a, i) => {
          const b = positions[(i + 1) % positions.length]!;
          const isActiveEdge = live && speakerIdx === i;
          return (
            <line
              key={`rr-${i}`}
              x1={cx + a.x}
              y1={cy + a.y}
              x2={cx + b.x}
              y2={cy + b.y}
              stroke={isActiveEdge ? "url(#edge-active)" : "hsl(var(--border-soft) / 0.7)"}
              strokeWidth={isActiveEdge ? 2 : 1}
              strokeDasharray={isActiveEdge ? "6 4" : "3 4"}
              opacity={isActiveEdge ? 1 : 0.55}
            >
              {isActiveEdge ? (
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="-20"
                  dur="0.8s"
                  repeatCount="indefinite"
                />
              ) : null}
            </line>
          );
        })}

      {topology === "moderator-led" && live && moderatorIdx >= 0
        ? positions.map((p, i) => {
            if (i === moderatorIdx) return null;
            const m = positions[moderatorIdx]!;
            const isActive = speakerIdx === i;
            return (
              <line
                key={`ml-${i}`}
                x1={cx + m.x}
                y1={cy + m.y}
                x2={cx + p.x}
                y2={cy + p.y}
                stroke={isActive ? "url(#edge-active)" : "hsl(var(--accent) / 0.4)"}
                strokeWidth={isActive ? 1.8 : 1.1}
                opacity={isActive ? 1 : 0.55}
              />
            );
          })
        : null}

      {topology === "moderator-led" && !live
        ? positions.map((p, i) => (
            <line
              key={`mlp-${i}`}
              x1={cx}
              y1={cy}
              x2={cx + p.x}
              y2={cy + p.y}
              stroke="hsl(var(--border-soft) / 0.6)"
              strokeWidth={1}
              strokeDasharray="2 6"
            />
          ))
        : null}

      {topology === "cross-fire" && live && speakerIdx >= 0 ? (
        (() => {
          const next = positions[(speakerIdx + 1) % positions.length]!;
          const cur = positions[speakerIdx]!;
          return (
            <line
              x1={cx + cur.x}
              y1={cy + cur.y}
              x2={cx + next.x}
              y2={cy + next.y}
              stroke="url(#edge-active)"
              strokeWidth={2.4}
              strokeLinecap="round"
            />
          );
        })()
      ) : null}

      {speakerIdx >= 0 ? (
        <circle
          cx={cx + positions[speakerIdx]!.x}
          cy={cy + positions[speakerIdx]!.y}
          r={48}
          fill="url(#edge-glow)"
          opacity={0.85}
        />
      ) : null}
    </svg>
  );
}
